// Handoff notes on one alert — the specialist↔AM conversation.
//
// ⚠️⚠️ THE BODY IS RENDERED AS TEXT, in `{note.body}`, and that is the whole
// safety model. The server stores it VERBATIM (a note quoting a snippet, or
// written with an angle bracket in it, is legitimate) — so the escaping has to
// happen here. React escapes by default. NEVER reach for
// `dangerouslySetInnerHTML` on this value, and never "render markdown" in it
// without a sanitiser: staff write these about client accounts and they are
// read by other staff, so the content is trusted no further than the author.

import { useState } from 'react';
import { MessageSquare } from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  NOTE_BODY_MAX,
  OLDER_NOTES_TOGGLE_LABEL,
  noteBodyError,
  splitNotesByRecency,
} from '../../lib/advertisingHome/alertNotes';
import type { AlertNote } from '../../types/advertisingHome';

function NoteLine({ note }: { note: AlertNote }): React.JSX.Element {
  const at = Date.parse(note.createdAt);
  const when = Number.isFinite(at)
    ? new Date(at).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
    : null;
  return (
    <li className="rounded-md border border-border bg-card px-2.5 py-2 text-sm">
      {/* ⚠️ TEXT. React escapes this; do not switch it to raw HTML. */}
      <p className="whitespace-pre-wrap break-words text-foreground">{note.body}</p>
      <p className="mt-1 text-xs text-muted-foreground">
        {note.authorName}
        {when ? ` · ${when}` : null}
      </p>
    </li>
  );
}

export interface AlertNotesProps {
  notes: readonly AlertNote[];
  onAdd: (body: string) => void;
  busy: boolean;
}

export function AlertNotes({ notes, onAdd, busy }: AlertNotesProps): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [showOlder, setShowOlder] = useState(false);
  const [draft, setDraft] = useState('');

  const { recent, older } = splitNotesByRecency(notes, Date.now());
  const error = draft.trim() ? noteBodyError(draft) : null;

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground"
      >
        <MessageSquare className="h-3.5 w-3.5" aria-hidden />
        {notes.length > 0 ? `Notes (${notes.length})` : 'Add a note'}
      </button>

      {open && (
        <div className="mt-2 space-y-2">
          {recent.length > 0 && (
            <ul className="space-y-1.5">
              {recent.map((n) => (
                <NoteLine key={n.id} note={n} />
              ))}
            </ul>
          )}

          {/* ⚠️ THE CAVEAT IS IN THE LABEL, not just in a code comment. There is
              no alert-EPISODE id, so a note from six weeks ago may be about a
              completely different firing of the same alert — and a reader given
              only "show earlier notes" will take it as context on today's. */}
          {older.length > 0 && !showOlder && (
            <button
              type="button"
              onClick={() => setShowOlder(true)}
              className="text-xs text-muted-foreground underline hover:text-foreground"
            >
              {OLDER_NOTES_TOGGLE_LABEL} ({older.length})
            </button>
          )}
          {showOlder && older.length > 0 && (
            <>
              <p className="text-xs text-muted-foreground">{OLDER_NOTES_TOGGLE_LABEL}</p>
              <ul className="space-y-1.5 opacity-80">
                {older.map((n) => (
                  <NoteLine key={n.id} note={n} />
                ))}
              </ul>
            </>
          )}

          {recent.length === 0 && older.length === 0 && (
            <p className="text-xs text-muted-foreground">
              No notes yet. A note stays with this alert after the clear expires — it is how
              the other board finds out what happened.
            </p>
          )}

          <div className="space-y-1">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={2}
              maxLength={NOTE_BODY_MAX}
              placeholder="e.g. waiting on the client to top up the card — don't clear"
              aria-label="Add a note"
              className="w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-sm"
            />
            {error && <p className="text-xs text-destructive">{error}</p>}
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="secondary"
                disabled={busy || Boolean(noteBodyError(draft))}
                onClick={() => {
                  onAdd(draft.trim());
                  setDraft('');
                }}
              >
                Add note
              </Button>
              {/* ⚠️ Append-only, and it says so: a handoff log that can be
                  rewritten is worth nothing, so nobody should go looking for an
                  edit button that was never built. */}
              <span className="text-xs text-muted-foreground">
                Notes can&rsquo;t be edited or deleted.
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
